import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { LocationScope } from '../../types/MenuInterface';

export type SaveLocationOption = LocationScope;

interface SaveLocationsButtonProps {
  className?: string;
  isDisabled?: boolean;
  onSelect: (option: SaveLocationOption) => void;
}

interface OptionsPosition {
  // only one of top / bottom is set, depending on whether the options open below or above the button
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

const OPTIONS: { value: SaveLocationOption; label: string }[] = [
  { value: 'single', label: 'This Location' },
  { value: 'all', label: 'All Locations' },
  { value: 'selected', label: 'Select Locations' }
];

const SaveLocationsButton = ({ className = '', isDisabled = false, onSelect }: SaveLocationsButtonProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState<OptionsPosition>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  // the options are portaled to <body> with fixed positioning so scrollable parents
  // (e.g. the hours picker, which has overflow: auto) can't clip them
  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        // open upwards when the button is too close to the bottom of the window to fit the options
        const optionsHeight = optionsRef.current?.offsetHeight ?? 0;
        const fitsBelow = rect.bottom + optionsHeight <= window.innerHeight;
        const opensAbove = !fitsBelow && rect.top - optionsHeight >= 0;
        setPosition(
          opensAbove
            ? { bottom: window.innerHeight - rect.top, left: rect.left, width: rect.width }
            : { top: rect.bottom, left: rect.left, width: rect.width }
        );
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    // capture scrolls from any scrollable ancestor, not just the window
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // close the options when clicking anywhere outside the button or its options
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !optionsRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleOptionClicked = (option: SaveLocationOption) => {
    setIsOpen(false);
    onSelect(option);
  };

  return (
    <div ref={containerRef} className={`save-locations-button ${className}`}>
      <button
        type="button"
        className="save-locations-button-toggle"
        disabled={isDisabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>SAVE</span>
        <svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.625 6.75L1.22238 1.22732" stroke="white" strokeLinecap="square" />
          <path d="M12.0054 1.25L6.60275 6.77268" stroke="white" strokeLinecap="square" />
        </svg>
      </button>
      {isOpen &&
        ReactDOM.createPortal(
          <ul
            ref={optionsRef}
            className={`save-locations-button-options${position?.bottom != null ? ' opens-above' : ''}`}
            role="menu"
            // rendered hidden until measured, so its height is known when choosing above or below
            style={position ?? { top: 0, left: 0, visibility: 'hidden' }}
          >
            {OPTIONS.map(({ value, label }) => (
              <li key={value} role="none">
                <button type="button" role="menuitem" onClick={() => handleOptionClicked(value)}>
                  {label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default SaveLocationsButton;
